import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex w-fit h-fit shrink-0 items-center justify-center gap-1 overflow-hidden border border-transparent font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-foreground focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline: "border-border text-foreground [a&]:hover:bg-surface-hover",
        "outline-muted":
          "border border-border bg-contrast/5 text-contrast/75 [a&]:hover:bg-contrast/8 dark:bg-white/5 dark:text-foreground/80 dark:[a&]:hover:bg-white/8",
        muted:
          "border border-border bg-contrast/5 text-contrast/75 [a&]:hover:bg-contrast/8 dark:border-transparent dark:bg-white/5 dark:text-foreground/80 dark:[a&]:hover:bg-white/8",
        /** Sequence draft — Figma `192:5331` (ARBI PRODUCT DESIGN) */
        draft:
          "border border-border bg-[rgba(255,183,102,0.1)] text-[#ffb766] !rounded-[6px] [a&]:hover:bg-[rgba(255,183,102,0.14)]",
        ghost: "[a&]:hover:bg-surface-hover",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        success:
          "bg-[rgba(102,220,126,0.1)] text-[#15803d] dark:text-[#66dc7e] border-[rgba(102,220,126,0.1)]",
        warning:
          "bg-[rgba(255,175,83,0.12)] text-[#b45309] dark:text-[#FFD083] border-[rgba(255,175,83,0.2)] dark:border-[#FFD083]/10",
        info:
          "bg-[rgba(76,154,255,0.1)] text-[#1e40af] dark:text-[#83b9ff] border-[rgba(76,154,255,0.15)] dark:border-[rgba(76,154,255,0.1)]",
        private:
          "bg-[rgba(255,208,131,0.08)] text-[#b45309] dark:text-[#ffd083] border-[rgba(255,208,131,0.2)] dark:border-[rgba(255,208,131,0.1)]",
        linear: cn(
          "border-none before:from-(--gradient-color)/40 before:to-(--gradient-color)/5",
          "border-composition",
          "before:bg-linear-to-t before:h-full before:w-full before:absolute before:top-0 before:left-0 overflow-hidden relative before:transition-all before:z-0 before:duration-300 before:pointer-events-none",
        ),
        credits: "bg-[#FFAF53]/1 text-[#FFAF53] border-[#FFAF53]/10",
        dark: "border-white/10 bg-surface-elevated/95 !rounded-md text-heading font-medium leading-4 backdrop-blur-[2px] [a&]:hover:bg-surface-elevated",
      },
      size: {
        none: "",
        xs: "rounded-sm px-1 py-0.5 text-xs",
        sm: "rounded-sm px-1.5 py-0.5 text-xs",
        md: "rounded-full px-2 py-0.5 text-xs",
        lg: "rounded-sm px-2 py-1 text-[0.8rem] tracking-tight",

        "icon-sm": "rounded-sm size-5.5 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

function Badge({
  className,
  variant = "default",
  size = "md",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
